"use strict";
/**
 * SOLID Review
 * S — Strong. One concern: given two revisions of the committed port-contract document, say what
 *     changed and whether a consumer built against the old one still works.
 * O — Good. A new JSON-schema keyword worth classifying is a new branch in `compareSchema`; the
 *     document walk above it does not change.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One function, three plain data types.
 * D — Strong. Pure over JSON. Depends on nothing — not on zod, not on the port classes.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.diffContractDocuments = diffContractDocuments;
function keyOf(entry) {
    return `${entry.component}.${entry.name} (${entry.kind})`;
}
function isSchema(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function diffContractDocuments(before, after) {
    const breaking = [];
    const dangerous = [];
    const was = new Map(before.ports.map((p) => [keyOf(p), p]));
    const now = new Map(after.ports.map((p) => [keyOf(p), p]));
    for (const [key, old] of was) {
        const current = now.get(key);
        if (current === undefined) {
            breaking.push(`${key}: port removed`);
            continue;
        }
        if (old.contract === null && current.contract !== null)
            dangerous.push(`${key}: contract added`);
        else if (old.contract !== null && current.contract === null)
            breaking.push(`${key}: contract removed`);
        else if (old.contract !== null && current.contract !== null) {
            compareContracts(key, old.contract, current.contract, breaking, dangerous);
        }
    }
    for (const key of now.keys()) {
        if (!was.has(key))
            dangerous.push(`${key}: port added`);
    }
    return { breaking, dangerous };
}
function compareContracts(key, b, a, breaking, dangerous) {
    if (b.local !== a.local)
        breaking.push(`${key}: local changed ${b.local} → ${a.local}`);
    if (b.stream !== a.stream)
        breaking.push(`${key}: stream changed ${b.stream} → ${a.stream}`);
    for (const op of Object.keys(b.ops)) {
        const after = a.ops[op];
        if (after === undefined) {
            breaking.push(`${key}.${op}: op removed`);
            continue;
        }
        const before = b.ops[op];
        compareSchema(`${key}.${op}.args`, before.args, after.args, 'args', breaking, dangerous);
        compareSchema(`${key}.${op}.result`, before.result, after.result, 'result', breaking, dangerous);
    }
    for (const op of Object.keys(a.ops)) {
        if (!(op in b.ops))
            dangerous.push(`${key}.${op}: op added`);
    }
    compareDefs(`${key}.$defs`, (b.defs ?? {}), (a.defs ?? {}), breaking, dangerous);
}
/**
 * A shared definition is reached from args AND results, and the two directions disagree about
 * what is safe (a field removed from an argument is dangerous; from a result, breaking). Judged
 * both ways: a change either direction calls breaking is breaking; the rest is dangerous.
 */
function compareDefs(path, b, a, breaking, dangerous) {
    const asResult = { breaking: [], dangerous: [] };
    const asArgs = { breaking: [], dangerous: [] };
    compareMap(path, b, a, 'result', asResult.breaking, asResult.dangerous);
    compareMap(path, b, a, 'args', asArgs.breaking, asArgs.dangerous);
    const worst = new Set([...asResult.breaking, ...asArgs.breaking]);
    for (const line of worst)
        breaking.push(line);
    for (const line of new Set([...asResult.dangerous, ...asArgs.dangerous]))
        if (!worst.has(line))
            dangerous.push(line);
}
const HANDLED = new Set(['type', 'enum', 'properties', 'required', 'prefixItems', 'items', 'anyOf', 'oneOf', '$defs', 'additionalProperties', 'description', '$schema', '$comment', 'title']);
function compareSchema(path, b, a, dir, breaking, dangerous) {
    if (!isSchema(b) || !isSchema(a)) {
        if (JSON.stringify(b) !== JSON.stringify(a))
            breaking.push(`${path}: schema changed`);
        return;
    }
    if (JSON.stringify(b['type']) !== JSON.stringify(a['type'])) {
        breaking.push(`${path}: type changed ${JSON.stringify(b['type'])} → ${JSON.stringify(a['type'])}`);
        return;
    }
    compareEnum(path, b, a, breaking, dangerous);
    compareProperties(path, b, a, dir, breaking, dangerous);
    compareTuple(path, b, a, dir, breaking, dangerous);
    if ('items' in b || 'items' in a)
        compareSchema(`${path}[]`, b['items'], a['items'], dir, breaking, dangerous);
    compareUnion(path, 'anyOf', b, a, breaking, dangerous);
    compareUnion(path, 'oneOf', b, a, breaking, dangerous);
    if (isSchema(b['$defs']) || isSchema(a['$defs'])) {
        compareMap(`${path}.$defs`, (b['$defs'] ?? {}), (a['$defs'] ?? {}), dir, breaking, dangerous);
    }
    // Everything this classifier does not model is compared textually and any change is breaking.
    // A tuple's min/maxItems restate its arity, which compareTuple has already judged.
    const isTuple = 'prefixItems' in b || 'prefixItems' in a;
    for (const keyword of new Set([...Object.keys(b), ...Object.keys(a)])) {
        if (HANDLED.has(keyword) || (isTuple && (keyword === 'minItems' || keyword === 'maxItems')))
            continue;
        if (JSON.stringify(b[keyword]) !== JSON.stringify(a[keyword]))
            breaking.push(`${path}: '${keyword}' changed`);
    }
}
function compareEnum(path, b, a, breaking, dangerous) {
    const bv = Array.isArray(b['enum']) ? b['enum'].map((v) => JSON.stringify(v)) : null;
    const av = Array.isArray(a['enum']) ? a['enum'].map((v) => JSON.stringify(v)) : null;
    if (bv === null && av === null)
        return;
    if (bv === null || av === null) {
        breaking.push(`${path}: enum ${bv === null ? 'introduced' : 'removed'}`);
        return;
    }
    for (const v of bv)
        if (!av.includes(v))
            breaking.push(`${path}: enum value ${v} removed`);
    for (const v of av)
        if (!bv.includes(v))
            dangerous.push(`${path}: enum value ${v} added`);
}
function compareProperties(path, b, a, dir, breaking, dangerous) {
    const bp = isSchema(b['properties']) ? b['properties'] : null;
    const ap = isSchema(a['properties']) ? a['properties'] : null;
    if (bp === null && ap === null)
        return;
    const bReq = new Set(Array.isArray(b['required']) ? b['required'] : []);
    const aReq = new Set(Array.isArray(a['required']) ? a['required'] : []);
    const before = bp ?? {};
    const after = ap ?? {};
    for (const field of Object.keys(before)) {
        const where = `${path}.${field}`;
        if (!(field in after)) {
            // A caller may still SEND a removed argument field (parsers strip it); a reader can no
            // longer READ a removed result field.
            (dir === 'args' ? dangerous : breaking).push(`${where}: field removed`);
            continue;
        }
        if (bReq.has(field) && !aReq.has(field)) {
            // Optional-ised: a caller may now omit it (safe); a reader may now find it missing (breaks).
            (dir === 'args' ? dangerous : breaking).push(`${where}: no longer required`);
        }
        else if (!bReq.has(field) && aReq.has(field)) {
            // Required-ised: a caller that omitted it is refused (breaks); a reader gets more (safe).
            (dir === 'args' ? breaking : dangerous).push(`${where}: now required`);
        }
        compareSchema(where, before[field], after[field], dir, breaking, dangerous);
    }
    for (const field of Object.keys(after)) {
        if (field in before)
            continue;
        const where = `${path}.${field}`;
        if (dir === 'args' && aReq.has(field))
            breaking.push(`${where}: required field added`);
        else
            dangerous.push(`${where}: field added`);
    }
}
function compareTuple(path, b, a, dir, breaking, dangerous) {
    const bt = Array.isArray(b['prefixItems']) ? b['prefixItems'] : null;
    const at = Array.isArray(a['prefixItems']) ? a['prefixItems'] : null;
    if (bt === null && at === null)
        return;
    if (bt === null || at === null || bt.length !== at.length) {
        // Positional arguments: any change in arity shifts or drops a position for every caller.
        breaking.push(`${path}: tuple arity changed ${bt?.length ?? 'none'} → ${at?.length ?? 'none'}`);
        return;
    }
    bt.forEach((item, i) => compareSchema(`${path}[${i}]`, item, at[i], dir, breaking, dangerous));
}
function compareUnion(path, keyword, b, a, breaking, dangerous) {
    const bu = Array.isArray(b[keyword]) ? b[keyword].map((m) => JSON.stringify(m)) : null;
    const au = Array.isArray(a[keyword]) ? a[keyword].map((m) => JSON.stringify(m)) : null;
    if (bu === null && au === null)
        return;
    if (bu === null || au === null) {
        breaking.push(`${path}: ${keyword} ${bu === null ? 'introduced' : 'removed'}`);
        return;
    }
    // Members are compared whole: a member that changed is one removed and one added, which is
    // reported as breaking — the conservative reading for a union on either side.
    for (const m of bu)
        if (!au.includes(m))
            breaking.push(`${path}: ${keyword} member removed`);
    for (const m of au)
        if (!bu.includes(m))
            dangerous.push(`${path}: ${keyword} member added`);
}
function compareMap(path, b, a, dir, breaking, dangerous) {
    for (const key of Object.keys(b)) {
        if (!(key in a))
            breaking.push(`${path}.${key}: definition removed`);
        else
            compareSchema(`${path}.${key}`, b[key], a[key], dir, breaking, dangerous);
    }
    for (const key of Object.keys(a))
        if (!(key in b))
            dangerous.push(`${path}.${key}: definition added`);
}
//# sourceMappingURL=contractDiff.js.map